import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Clock, MoreHorizontal, Plus, ShieldCheck, X } from "lucide-react";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";
import { Member, MemberRole, membersApi, membersErr } from "../../api/members/membersApi";
import "../account/account.scss";
import "./members.scss";

/**
 * Settings → Members: the people who can sign in to this org's dashboard.
 * Org admins invite colleagues, set roles, deactivate, remove access and
 * reset a locked-out colleague's 2FA. Platform staff visible in the org are
 * listed read-only.
 */
const MembersPage: React.FC = () => {
  const { user } = useUser();
  const tenantId = user?.org_id || "";

  const [members, setMembers] = useState<Member[] | null>(null);
  const [roles, setRoles] = useState<MemberRole[]>([]);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [approving, setApproving] = useState<Member | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError("");
    try {
      const [m, r] = await Promise.all([membersApi.list(tenantId), membersApi.roles(tenantId)]);
      setMembers(m.data);
      setRoles(r.data);
    } catch (e) {
      setError(membersErr(e, "Could not load members"));
      setMembers([]);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (key: string, fn: () => Promise<{ data: { message: string } }>) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(r.data.message);
      await load();
    } catch (e) {
      toast.error(membersErr(e, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  const roleLabel = (k: string) => roles.find((r) => r.key === k)?.display_name || k;
  const pending = (members || []).filter((m) => m.pending_approval);

  return (
    <div className="members">
      <div className="members__head">
        <div>
          <h2>Members</h2>
          <p>People who can sign in to this dashboard. Invites arrive by email. Sign-in options live under Settings → Sign-in methods.</p>
        </div>
        <button className="acct-btn acct-btn--primary" onClick={() => setShowInvite(true)}>
          <Plus size={16} /> Invite member
        </button>
      </div>

      {error && (
        <div className="acct-notice acct-notice--error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {pending.length > 0 && (
        <div className="acct-notice acct-notice--warn" style={{ marginBottom: 16, display: "block" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <Clock size={16} /> {pending.length} access request{pending.length > 1 ? "s" : ""} waiting
          </div>
          <div style={{ marginTop: 4 }}>
            These people signed in with your company single sign-on but have no access yet.
            Approve them with a role, or reject to remove the account.
          </div>
        </div>
      )}

      {members === null ? (
        <p className="acct-loading">Loading members...</p>
      ) : members.length === 0 ? (
        <p className="acct-empty">No members yet. Invite your first colleague.</p>
      ) : (
        <div className="members__table-wrap">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Roles</th>
                <th>Status</th>
                <th>2FA</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <MemberRow
                  key={m.user_id}
                  m={m}
                  roleLabel={roleLabel}
                  busy={busy}
                  onEditRoles={() => setEditing(m)}
                  onApprove={() => setApproving(m)}
                  onReject={() => {
                    if (window.confirm(`Reject ${m.display_name || m.email}? Their account will be removed; they can request again later.`))
                      act(`reject:${m.user_id}`, () => membersApi.reject(tenantId, m.user_id));
                  }}
                  onResend={() => act(`resend:${m.user_id}`, () => membersApi.resendInvite(tenantId, m.user_id))}
                  onDeactivate={() => {
                    if (window.confirm(`Deactivate ${m.display_name || m.email}? They will not be able to sign in.`))
                      act(`deact:${m.user_id}`, () => membersApi.deactivate(tenantId, m.user_id));
                  }}
                  onReactivate={() => act(`react:${m.user_id}`, () => membersApi.reactivate(tenantId, m.user_id))}
                  onReset2fa={() => {
                    if (window.confirm(`Reset two-factor authentication for ${m.display_name || m.email}? They will sign in with just their password until they set it up again.`))
                      act(`2fa:${m.user_id}`, () => membersApi.reset2fa(tenantId, m.user_id));
                  }}
                  onRemove={() => {
                    if (window.confirm(`Remove ${m.display_name || m.email} from this dashboard? This revokes their access.`))
                      act(`rm:${m.user_id}`, () => membersApi.remove(tenantId, m.user_id));
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}


      {showInvite && (
        <InviteModal
          tenantId={tenantId}
          roles={roles}
          onClose={() => setShowInvite(false)}
          onDone={() => {
            setShowInvite(false);
            load();
          }}
        />
      )}

      {editing && (
        <RolesModal
          tenantId={tenantId}
          member={editing}
          roles={roles}
          roleLabel={roleLabel}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {approving && (
        <RolesModal
          tenantId={tenantId}
          member={approving}
          roles={roles}
          roleLabel={roleLabel}
          approve
          onClose={() => setApproving(null)}
          onDone={() => {
            setApproving(null);
            load();
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------- row

const MemberRow: React.FC<{
  m: Member;
  roleLabel: (k: string) => string;
  busy: string | null;
  onEditRoles: () => void;
  onApprove: () => void;
  onReject: () => void;
  onResend: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onReset2fa: () => void;
  onRemove: () => void;
}> = ({ m, roleLabel, busy, onEditRoles, onApprove, onReject, onResend, onDeactivate, onReactivate, onReset2fa, onRemove }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const active = m.state === "USER_STATE_ACTIVE";
  const name = m.display_name || `${m.first_name} ${m.last_name}`.trim() || m.username;
  const rowBusy = !!busy && busy.endsWith(`:${m.user_id}`);

  return (
    <tr>
      <td>
        <div className="members__who">
          <div className="avatar">{(name || "?").charAt(0).toUpperCase()}</div>
          <div>
            <strong>
              {name} {m.is_self && <span className="acct-badge acct-badge--muted">You</span>}
            </strong>
            <span>{m.email || m.username}</span>
          </div>
        </div>
      </td>
      <td>
        <div className="members__roles">
          {m.role_keys.length ? m.role_keys.map((k) => <span className="role-chip" key={k}>{roleLabel(k)}</span>) : <span className="acct-badge acct-badge--muted">No role</span>}
        </div>
      </td>
      <td>
        {m.pending_approval ? (
          <span className="acct-badge acct-badge--warn"><Clock size={12} /> Awaiting approval</span>
        ) : m.pending_invite ? (
          <span className="acct-badge acct-badge--warn">Invited</span>
        ) : active ? (
          <span className="acct-badge acct-badge--ok"><CheckCircle2 size={12} /> Active</span>
        ) : (
          <span className="acct-badge acct-badge--muted">Deactivated</span>
        )}
      </td>
      <td>
        {m.mfa_enabled ? (
          <span className="acct-badge acct-badge--ok"><ShieldCheck size={12} /> On</span>
        ) : (
          <span className="acct-badge acct-badge--muted">Off</span>
        )}
      </td>
      <td>
        <div className="members__actions">
          {!m.manageable ? (
            <span className="acct-badge acct-badge--muted" title="Managed by ChargingAdda">Platform</span>
          ) : m.pending_approval ? (
            <>
              <button className="acct-btn acct-btn--small acct-btn--primary" disabled={rowBusy} onClick={onApprove}>
                Approve
              </button>
              <button className="acct-btn acct-btn--small acct-btn--danger" disabled={rowBusy} onClick={onReject}>
                {rowBusy ? "..." : "Reject"}
              </button>
            </>
          ) : m.is_self ? null : (
            <div className="member-menu" ref={ref}>
              <button className="acct-btn acct-btn--small" disabled={rowBusy} onClick={() => setOpen((o) => !o)} aria-label="Actions">
                {rowBusy ? "..." : <MoreHorizontal size={16} />}
              </button>
              {open && (
                <div className="member-menu__list" onClick={() => setOpen(false)}>
                  <button onClick={onEditRoles}>Change roles</button>
                  {m.pending_invite && <button onClick={onResend}>Resend invite</button>}
                  {m.mfa_enabled && <button onClick={onReset2fa}>Reset two-factor</button>}
                  {active ? (
                    <button onClick={onDeactivate}>Deactivate</button>
                  ) : !m.pending_invite ? (
                    <button onClick={onReactivate}>Reactivate</button>
                  ) : null}
                  <button className="danger" onClick={onRemove}>Remove access</button>
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------- role picker

const RolePicker: React.FC<{
  roles: MemberRole[];
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ roles, value, onChange }) => (
  <div className="role-picker">
    {roles.map((r) => (
      <label key={r.key}>
        <input
          type="checkbox"
          checked={value.includes(r.key)}
          onChange={() => onChange(value.includes(r.key) ? value.filter((k) => k !== r.key) : [...value, r.key])}
        />
        <span>
          {r.display_name || r.key}
          {r.group && <small>{r.group}</small>}
        </span>
      </label>
    ))}
  </div>
);

// ---------------------------------------------------------------- invite

const InviteModal: React.FC<{
  tenantId: string;
  roles: MemberRole[];
  onClose: () => void;
  onDone: () => void;
}> = ({ tenantId, roles, onClose, onDone }) => {
  const [f, setF] = useState({ email: "", first_name: "", last_name: "" });
  const [picked, setPicked] = useState<string[]>(roles.length === 1 ? [roles[0].key] : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (picked.length === 0) return setError("Pick at least one role.");
    setBusy(true);
    setError("");
    try {
      const r = await membersApi.invite(tenantId, { ...f, role_keys: picked });
      toast.success(`Invite sent to ${r.data.email || f.email}`);
      onDone();
    } catch (err) {
      setError(membersErr(err, "Could not send the invite"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct-modal-overlay" onClick={onClose}>
      <form className="acct-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="acct-modal__head">
          <h3>Invite member</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <p>They will get an email with a link to choose their password and sign in.</p>
        <div className="acct-form">
          <label>
            First name
            <input autoFocus value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required maxLength={200} />
          </label>
          <label>
            Last name
            <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} required maxLength={200} />
          </label>
          <label className="full">
            Work email
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          </label>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 8 }}>Roles</div>
          <RolePicker roles={roles} value={picked} onChange={setPicked} />
        </div>
        {error && <p className="acct-form-error">{error}</p>}
        <div className="acct-actions">
          <button type="button" className="acct-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="acct-btn acct-btn--primary" disabled={busy}>
            {busy ? "Sending..." : "Send invite"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------------------------------------------------------------- roles

const RolesModal: React.FC<{
  tenantId: string;
  member: Member;
  roles: MemberRole[];
  roleLabel: (k: string) => string;
  approve?: boolean;
  onClose: () => void;
  onDone: () => void;
}> = ({ tenantId, member, roles, approve = false, onClose, onDone }) => {
  const [picked, setPicked] = useState<string[]>(member.role_keys.length ? member.role_keys : roles.length === 1 ? [roles[0].key] : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (picked.length === 0) return setError("Pick at least one role.");
    setBusy(true);
    setError("");
    try {
      if (approve) {
        await membersApi.approve(tenantId, member.user_id, picked);
        toast.success(`${member.display_name || member.email} approved`);
      } else {
        await membersApi.updateRoles(tenantId, member.user_id, picked);
        toast.success("Roles updated");
      }
      onDone();
    } catch (err) {
      setError(membersErr(err, approve ? "Could not approve" : "Could not update roles"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct-modal-overlay" onClick={onClose}>
      <div className="acct-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acct-modal__head">
          <h3>{approve ? "Approve" : "Roles"} — {member.display_name || member.email}</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {approve && <p>Pick the role(s) this person should have. They will get access the next time they sign in.</p>}
        <RolePicker roles={roles} value={picked} onChange={setPicked} />
        {error && <p className="acct-form-error">{error}</p>}
        <div className="acct-actions">
          <button type="button" className="acct-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="acct-btn acct-btn--primary" disabled={busy} onClick={save}>
            {busy ? (approve ? "Approving..." : "Saving...") : approve ? "Approve" : "Save roles"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MembersPage;
